import { MapPin, Navigation } from 'lucide-react'
import { osmEmbedSrc } from './_lib/bookingLocationMap'
import { locationKindCopy, type LocationKind } from '@/lib/locationKind'

export type Pickup = {
  name: string
  /** Whether this place is a lakeside meet or a shop you collect gear at,
   *  derived from `tenant_locations.location_type`. Drives the wording — see
   *  locationKind.ts for why the copy is derived rather than configured. */
  kind?: LocationKind
  /** Customer-facing short label (`tenant_locations.short_name`), when the
   *  dealer set one. `name` is their internal label and often reads like it.
   *  This page shows `name` on purpose — it sits under a "Pickup location"
   *  heading where the full detail helps. The booking_widget embed, which drops
   *  a single line onto a marketing page, prefers this. */
  shortName?: string | null
  locality: string | null
  latitude: number | null
  longitude: number | null
  directionsHref: string | null
}

/**
 * Turo-style pickup-location panel: a calm header (pin + name + locality) over a
 * free OpenStreetMap embed centred on the dealer's dropped pin, with a
 * "Get directions" link out to the customer's own maps app. The map only renders
 * when the dealer set coordinates; otherwise the panel degrades to the name +
 * locality + directions link (no empty tile).
 */
export function LocationMap({ pickup }: { pickup: Pickup }) {
  const embedSrc = osmEmbedSrc(pickup.latitude, pickup.longitude)
  const copy = locationKindCopy(pickup.kind ?? 'unknown')

  return (
    <section className="mt-12 border-t border-[var(--color-border)] pt-10">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-[var(--color-text-primary)]">
        <MapPin className="h-5 w-5 text-[var(--color-cta-readable)]" aria-hidden="true" />
        {copy.heading}
      </h2>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2 text-sm text-[var(--color-text-secondary)]">
        <span className="font-medium text-[var(--color-text-primary)]">{pickup.name}</span>
        {pickup.locality && <span>· {pickup.locality}</span>}
      </div>
      {copy.note && (
        <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{copy.note}</p>
      )}

      {embedSrc && (
        <iframe
          title={`Map showing ${pickup.name}`}
          src={embedSrc}
          loading="lazy"
          className="mt-4 aspect-[16/9] w-full rounded-lg border border-[var(--color-border)]"
        />
      )}

      {pickup.directionsHref && (
        <a
          href={pickup.directionsHref}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-cta-readable)] hover:underline"
        >
          <Navigation className="h-4 w-4" aria-hidden="true" />
          Get directions
        </a>
      )}
    </section>
  )
}
